/**
 * TimeZoneNote — the one subtle "times are shown in …" line.
 *
 * Every surface that renders a time which may not be in the reader's own zone
 * says so the SAME way (2026-08-07, Pat): the staff editor's profile row, the
 * appointment record, and the booking sheet all render this, so a user learns
 * the cue once. Presentational only — the caller composes the sentence; this
 * owns the ink, size, icon and spacing so the pattern cannot fork.
 *
 * Design-consistency gate: token-only (T.soft / T.n6 / T.border), 11.5px —
 * the same treatment as the booking sheet's original inline tz note, which
 * this replaces.
 */
import type { ReactNode } from "react";
import { Icon, T } from "@v0y4ge/jrni-ui";
import { tzShortLabel } from "../lib/datetime";

/** "Europe/Zurich (GMT+2)" — the label every zone is named by. */
export function zoneLabel(tz: string | null | undefined): string {
  if (!tz) return "";
  const off = tzShortLabel(tz);
  return off ? `${tz} (${off})` : tz;
}

export function TimeZoneNote({ children, tone = "quiet", testid }: {
  children: ReactNode;
  /** `quiet` = inline caption under a field; `panel` = boxed, for a form row. */
  tone?: "quiet" | "panel";
  testid?: string;
}) {
  const panel = tone === "panel";
  return (
    <div data-testid={testid}
      style={{
        display: "flex", alignItems: "flex-start", gap: 7,
        fontSize: 11.5, lineHeight: 1.5, color: T.soft,
        margin: panel ? "8px 0 0" : "2px 0 0",
        padding: panel ? "9px 11px" : 0,
        borderRadius: panel ? 9 : 0,
        background: panel ? T.n6 : "transparent",
        border: panel ? `1px solid ${T.border}` : "none",
      }}>
      <span style={{ color: T.soft, flexShrink: 0, marginTop: 1, display: "inline-flex" }}><Icon.clock size={12} /></span>
      <span style={{ minWidth: 0 }}>{children}</span>
    </div>
  );
}
